import { useSEO, generateWebPageSchema, generateBreadcrumbSchema, generateServiceSchema } from '@/utils/seo';
import ServicePageLayout, { ServicePageConfig } from '@/pages/service-common/ServicePageLayout';

const TECH_STACK = [
  { name: 'React / Next.js', icon: 'ri-reactjs-line' },
  { name: 'TypeScript', icon: 'ri-code-s-slash-line' },
  { name: 'Node.js', icon: 'ri-server-line' },
  { name: 'Python / AI', icon: 'ri-robot-2-line' },
  { name: 'AWS', icon: 'ri-cloud-line' },
  { name: 'Google Cloud', icon: 'ri-cloud-line' },
  { name: 'PostgreSQL', icon: 'ri-database-2-line' },
  { name: 'Supabase', icon: 'ri-database-line' },
];

const TechStack = () => (
  <div className="mt-2">
    <div className="mb-6">
      <p className="text-xs tracking-widest uppercase font-medium mb-3" style={{ color: '#7C3AED' }}>Tech Stack</p>
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">主要技術スタック</h2>
    </div>
    <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
      {TECH_STACK.map((t, i) => (
        <div
          key={i}
          className="flex flex-col items-center gap-2 p-4 rounded-xl border border-gray-100 transition-all duration-200"
          style={{ background: '#FAFAFA' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = '#7C3AED'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = '#F3F4F6'; }}
        >
          <i className={`${t.icon} text-2xl`} style={{ color: '#7C3AED' }}></i>
          <span className="text-xs text-gray-500 text-center">{t.name}</span>
        </div>
      ))}
    </div>
  </div>
);

/* ─── Hero Background ─── */
const HERO_IMG = 'https://readdy.ai/api/search-image?query=Elegant%20abstract%20golden%20circuit%20board%20patterns%20and%20code%20streams%20on%20deep%20navy%20blue%20background%20minimal%20sophisticated%20high-end%20corporate%20aesthetic%20soft%20glowing%20particles%20no%20text&width=1920&height=800&seq=syshero1&orientation=landscape';

const config: ServicePageConfig = {
  pageLabel: 'システム開発',
  heroImage: HERO_IMG,
  tag: 'System Development',
  title: <>生成AIを組み込んだ<br />次世代システム開発</>,
  subtitle: '生成AIとクラウドネイティブ技術を組み合わせた、高速・高品質なシステム開発。マーケティング自動化からデータプラットフォームまで対応します。',
  icon: 'ri-code-box-line',
  services: [
    { icon: 'ri-apps-line', title: 'カスタムソフトウェア開発', desc: '貴社固有の課題を解決するオーダーメイドのアプリケーションを開発します。' },
    { icon: 'ri-cpu-line', title: 'マーケティングオートメーション', desc: 'マーケティング業務の自動化システムを構築し、運用効率を飛躍的に向上させます。' },
    { icon: 'ri-hard-drive-2-line', title: 'データプラットフォーム構築', desc: '堅牢なデータ収集・蓄積・活用基盤を設計し、データ活用を推進します。' },
    { icon: 'ri-plug-line', title: 'API開発・システム連携', desc: '既存システムとのシームレスな連携を実現するAPI設計・開発を行います。' },
    { icon: 'ri-cloud-line', title: 'クラウドソリューション', desc: 'AWS・GCPなどを活用した、スケーラブルで高可用性のクラウド基盤を構築します。' },
    { icon: 'ri-robot-2-line', title: '生成AI組み込み開発', desc: '最新の生成AI技術を既存システムやワークフローに統合し、業務を革新します。' },
  ],
  steps: [
    { title: '要件定義・設計', desc: 'ビジネス課題を深く理解し、最適なアーキテクチャと技術スタックを選定します。' },
    { title: 'アジャイル開発', desc: '短いスプリントで素早く成果を出しながら、柔軟に要件変更に対応します。' },
    { title: 'テスト・品質保証', desc: '自動テストと徹底的なQAで、リリース後の安定稼働を確保します。' },
    { title: 'デプロイ・保守', desc: 'CI/CDパイプラインによるスムーズなリリースと、継続的な運用改善を実施します。' },
  ],
  benefits: [
    '生成AI活用で開発スピードが10倍以上に向上し、コストも大幅に削減',
    'マーケティング業務の自動化で、運用コストを削減しながら精度を向上',
    'スケーラブルなアーキテクチャで、ビジネス成長に合わせてシステムを拡張',
    '既存システムとのシームレスな連携で、段階的なDXを実現',
    '最新のクラウド技術活用で、セキュリティと可用性を高水準で確保',
    'アジャイル開発で早期に成果を検証しながら、最適解を追求',
  ],
  extraSection: <TechStack />,
};

export default function SystemDevelopmentPage() {
  useSEO({
    title: '東京 システム開発 TradFit株式会社 | 生成AI・クラウドネイティブ開発',
    description: 'TradFit株式会社のシステム開発サービス。生成AIとクラウドネイティブ技術を組み合わせた、高速・高品質なシステム開発を提供します。',
    keywords: 'システム開発, 生成AI, クラウド開発, マーケティングオートメーション, API開発, 東京',
    path: '/system-development',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema('システム開発 | TradFit株式会社', 'TradFit株式会社のシステム開発サービス。', '/system-development'),
        generateBreadcrumbSchema([{ name: 'ホーム', path: '/' }, { name: '事業内容', path: '/business' }, { name: 'システム開発', path: '/system-development' }]),
        generateServiceSchema('システム開発', '生成AIとクラウドネイティブ技術を組み合わせたシステム開発サービス。'),
      ],
    },
  });
  return <ServicePageLayout config={config} />;
}
